import { useParams, Link, useNavigate } from "react-router-dom";
import { useMemo, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CartSidebar from "@/components/feature/CartSidebar";
import ProductImages from "./components/ProductImages";
import ProductInfo from "./components/ProductInfo";
import ProductReviews from "./components/ProductReviews";
import RelatedCarousel from "./components/RelatedCarousel";
import { useProduct, useProducts } from "@/hooks/useProducts";

const CATEGORY_LABELS: Record<string, string> = {
  clothes: "i-clothes",
  shoes: "i-shoes",
  bags: "i-bag",
  nails: "i-nails",
};

const CATEGORY_ROUTES: Record<string, string> = {
  clothes: "/collections/clothes",
  shoes: "/collections/shoes",
  bags: "/collections/bags",
  nails: "/collections/nails",
};

export default function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const { product, loading: productLoading } = useProduct(slug ?? "");
  const { products: relatedProducts, loading: relatedLoading } = useProducts(
    product ? { category: product.category } : undefined
  );

  const related = useMemo(() => {
    if (!product) return [];
    return relatedProducts
      .filter((p) => p.id !== product.id)
      .slice(0, 8);
  }, [product, relatedProducts]);

  if (productLoading) {
    return (
      <div className="min-h-screen bg-moonlight flex flex-col items-center justify-center">
        <p className="text-sm tracking-widest uppercase text-midnight/40 animate-pulse">Loading...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-moonlight flex flex-col items-center justify-center gap-4">
        <p className="text-2xl font-light text-midnight/40" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Product Not Found
        </p>
        <button
          onClick={() => navigate(-1)}
          className="text-xs tracking-widest uppercase text-midnight border-b border-midnight/30 pb-0.5 cursor-pointer whitespace-nowrap"
        >
          Go Back
        </button>
      </div>
    );
  }

  const categoryRoute = CATEGORY_ROUTES[product.category] ?? "/";
  const categoryLabel = CATEGORY_LABELS[product.category] ?? product.category;

  return (
    <div className="min-h-screen bg-moonlight" style={{ fontFamily: "'Jost', sans-serif" }}>
      <Navbar />
      <CartSidebar />

      <main className="pt-20 lg:pt-24">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-5 lg:px-16 py-4">
          <nav className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-midnight/40">
            <Link to="/" className="hover:text-midnight transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to={categoryRoute} className="hover:text-midnight transition-colors cursor-pointer">
              {categoryLabel}
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-midnight/70 truncate max-w-xs">{product.name}</span>
          </nav>
        </div>

        {/* Product Section */}
        <section className="max-w-7xl mx-auto px-5 lg:px-16 pb-16">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Left: Images */}
            <div className="w-full lg:w-[58%]">
              <ProductImages images={product.images} name={product.name} />
            </div>

            {/* Right: Product Info */}
            <div className="w-full lg:w-[42%] lg:sticky lg:top-24 lg:self-start">
              <ProductInfo product={product} />
            </div>
          </div>
        </section>

        {/* Story Banner */}
        <section className="bg-midnight/4 border-y border-pearl/30 py-10 mb-16">
          <div className="max-w-7xl mx-auto px-5 lg:px-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { icon: "ri-leaf-line", title: "Sustainably Crafted", desc: "Ethically sourced materials" },
                { icon: "ri-hand-heart-line", title: "Handfinished", desc: "Every detail attended to" },
                { icon: "ri-global-line", title: "Global Delivery", desc: "Ships to 50+ countries" },
                { icon: "ri-award-line", title: "Quality Promise", desc: "30-day satisfaction guarantee" },
              ].map((item) => (
                <div key={item.title} className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className={`${item.icon} text-2xl text-midnight/40`} />
                  </div>
                  <p className="text-[11px] tracking-widest uppercase text-midnight/70 font-medium">{item.title}</p>
                  <p className="text-xs text-midnight/40">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <ProductReviews product={product} />

        {/* Related Products */}
        {related.length > 0 && (
          <section className="max-w-7xl mx-auto px-5 lg:px-16 pb-20">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-[10px] tracking-widest uppercase text-midnight/40 mb-1">You May Also Love</p>
                <h2
                  className="text-2xl md:text-3xl font-light text-midnight"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Complete the Look
                </h2>
              </div>
              <Link
                to={categoryRoute}
                className="text-[10px] tracking-widest uppercase text-midnight/50 border-b border-midnight/20 pb-0.5 hover:text-midnight hover:border-midnight transition-colors cursor-pointer whitespace-nowrap"
              >
                View All {categoryLabel}
              </Link>
            </div>
            <RelatedCarousel products={related} />
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
